import { RubyGemMark } from '../../components/RubyGemMark';
import { useState } from 'react';
import { FolderInput, Tags } from 'lucide-react';
import { FieldLabel } from '@documents-ui';
import type { DocumentKind, Folder, LegalDocument } from '@platform/shared-types';
import SearchableDropdown from '../../components/SearchableDropdown';
import { documentsApi } from '../../services/documentsApi';
import { toastService } from '../../services/toastService';
import { useDocumentCategories } from './documentCategories';
import { Button, ModalShell, folderOptions } from './rubyChrome';

/**
 * MOVE A DOCUMENT — folder and/or category, from the list it is sitting in.
 *
 * Both lists (Policies and Agreements) open this, because filing a document in
 * the wrong place is the ordinary mistake and having to open the document,
 * find Settings and save from there is a long way to go to fix it.
 *
 * THE TWO FIELDS ARE DIFFERENT QUESTIONS and the copy says so:
 *
 *   • FOLDER is how an OPERATOR files it. Free text, nested at will, renamed
 *     whenever. Nothing in the product keys on it.
 *   • CATEGORY is where the PRODUCT offers it — the axis pickers filter on, so
 *     a document with no category appears in no picker at all.
 *
 * Moving a document between folders is therefore always safe; changing its
 * category changes where it shows up in the app, which is why that half warns.
 * Neither touches a version, so nothing already signed is affected: a signature
 * binds a version, not a filing decision.
 */
export default function MoveDocumentModal({
  document: doc, folders, kind, mode = 'both', onClose, onMoved,
}: {
  document: Pick<LegalDocument, 'documentId' | 'title' | 'folderId' | 'category'>;
  folders: readonly Folder[];
  /** Narrows the category list to what belongs on this document's own list. */
  kind: DocumentKind;
  /**
   * WHICH QUESTION IS BEING ASKED. A row menu offers "Move to folder" and
   * "Change category" as separate items because they are separate intents, and
   * a modal that answers a question the operator did not ask invites them to
   * change something by accident — recategorising is the half that moves a
   * document between pickers. `both` keeps the original two-field form.
   */
  mode?: 'both' | 'folder' | 'category';
  onClose: () => void;
  onMoved: () => void;
}) {
  const { options: categoryChoices, loading: categoriesLoading } = useDocumentCategories(kind);

  const [folderId, setFolderId] = useState<string | null>(doc.folderId);
  const [category, setCategory] = useState<string | null>(doc.category ?? null);
  const [saving, setSaving] = useState(false);

  const showFolder = mode !== 'category';
  const showCategory = mode !== 'folder';

  const folderChanged = showFolder && !!folderId && folderId !== doc.folderId;
  const categoryChanged = showCategory && (category ?? null) !== (doc.category ?? null);
  const changed = folderChanged || categoryChanged;

  const currentFolderName = folders.find((f) => f.folderId === doc.folderId)?.name;

  const save = async () => {
    if (!changed || saving) return;
    setSaving(true);
    try {
      await documentsApi.updateDocument(doc.documentId, {
        ...(folderChanged ? { folderId: folderId! } : {}),
        // `undefined` would be dropped by the PATCH body, so clearing a
        // category is deliberately NOT offered here: a document that should
        // appear nowhere is retired, not un-categorised.
        ...(categoryChanged && category ? { category } : {}),
      });
      toastService.success(
        folderChanged && categoryChanged ? 'Moved and recategorised'
          : folderChanged ? 'Moved to another folder'
            : 'Category changed',
      );
      onMoved();
    } catch (e: any) {
      toastService.error(e?.message || 'document_update_failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={(
        <span className="flex items-center gap-2">
          {mode === 'category'
            ? <Tags size={17} className="text-admin-primary" />
            : <FolderInput size={17} className="text-admin-primary" />}
          {mode === 'category' ? 'Change category' : mode === 'folder' ? 'Move to folder' : 'Move document'}
        </span>
      )}
      subtitle={doc.title}
      onClose={onClose}
      footerNote="Filing only — no version is touched, and nothing already signed changes."
      footerActions={(
        <>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={() => void save()} disabled={!changed || saving}>
            {saving ? <span className="flex items-center gap-2"><RubyGemMark size={16} drawing color="currentColor" />Saving…</span> : 'Save'}
          </Button>
        </>
      )}
    >
      <div className="space-y-5">
        {showFolder && (
        <div>
          <FieldLabel>Folder</FieldLabel>
          <SearchableDropdown
            options={folderOptions(folders)}
            value={folderId}
            onChange={setFolderId}
            placeholder={folders.length ? 'Choose a folder' : 'No folders yet'}
            disabled={folders.length === 0}
          />
          <p className="mt-1.5 text-[11.5px] text-stone-500">
            How you file it. Sub-folders are shown nested.{' '}
            {currentFolderName && <>Currently in <b className="text-stone-700">{currentFolderName}</b>.</>}
            {' '}Folders are shared between Policies and Agreements, and nothing in the product
            keys on them — moving a document between folders never changes where it is offered.
          </p>
        </div>
        )}

        {showCategory && (
        <div>
          <FieldLabel>Category</FieldLabel>
          <SearchableDropdown
            options={categoryChoices}
            value={category}
            onChange={setCategory}
            placeholder={categoriesLoading ? 'Loading categories…' : 'Where is this offered?'}
          />
          <p className="mt-1.5 text-[11.5px] text-stone-500">
            Where the product offers it — a device finance agreement on the finance option, a
            cookie policy on the website. This is the half that pickers read.
          </p>
          {categoryChanged && (
            <p className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[11.5px] text-amber-800">
              Changing the category changes where this document appears in the app. Anything
              already signed keeps the version it was signed against.
            </p>
          )}
        </div>
        )}
      </div>
    </ModalShell>
  );
}
